/**
 * Pick the canonical branding logo URL from a user profile.
 *
 * Backend exposes:
 *   - `partnerLogo` — signed URL for the partner default logo.
 *   - `logo`       — user's custom branding when set, else partner default.
 *
 * Prefer the user's custom `logo` when it differs from `partnerLogo`; otherwise
 * use the partner default. Treat empty strings as missing values.
 *
 * @param {{ partnerLogo?: string | null, logo?: string | null } | null | undefined} profile
 * @returns {string | null}
 */
export function resolveBrandingLogo(profile) {
  if (!profile || typeof profile !== "object") return null;

  const stripQuery = (url) =>
    typeof url === "string" ? url.split("?")[0] : "";

  const partnerLogo =
    typeof profile.partnerLogo === "string" && profile.partnerLogo.length > 0
      ? profile.partnerLogo
      : null;

  const logo =
    typeof profile.logo === "string" && profile.logo.length > 0
      ? profile.logo
      : null;

  if (logo) {
    if (!partnerLogo || stripQuery(logo) !== stripQuery(partnerLogo)) {
      return logo;
    }
  }

  if (partnerLogo) return partnerLogo;
  return null;
}

/**
 * User-owned branding URL for editor surfaces that distinguish "My Logo" from
 * the partner default. Falls back to `thumbnail` when the backend keeps it in
 * sync with a branding upload.
 *
 * @param {{ logo?: string | null, thumbnail?: string | null, partnerLogo?: string | null } | null | undefined} profile
 * @returns {string | null}
 */
export function resolveUserBrandingLogo(profile) {
  if (!profile || typeof profile !== "object") return null;

  const stripQuery = (url) =>
    typeof url === "string" ? url.split("?")[0] : "";

  const partnerLogo =
    typeof profile.partnerLogo === "string" && profile.partnerLogo.length > 0
      ? profile.partnerLogo
      : null;

  const candidates = [profile.logo, profile.thumbnail].filter(
    (value) => typeof value === "string" && value.length > 0
  );

  for (const candidate of candidates) {
    if (!partnerLogo || stripQuery(candidate) !== stripQuery(partnerLogo)) {
      return candidate;
    }
  }

  return null;
}
